/* eslint-disable import/prefer-default-export */
import axios from '../axios';

// ---------------------------------------------------------------------------
// Financeiro / Emissão de cobranças — Service Layer (parcial)
//
// ⚠️ MIGRAÇÃO PARCIAL, DE PROPÓSITO (migrate-on-touch do FC1 A.10). Já moram
// aqui: EMISSÃO (adendo 0079), LISTAGEM/EXPORT (0082), BAIXA/PIX (0090) e
// DETALHE/TIMELINE (0105). A página BillingCharge ainda chama inline:
// PUT /billing/:id, POST/DELETE /billing/:id/updates e GET /billing/origins;
// o BillingGroupShow ainda chama GET /billing/group/:id inline. Migram quando
// forem tocadas.
//
// Contexto do adendo: a chave da ASAAS deixou de ser global. Toda cobrança sai
// de uma CONTA específica (`bank_account_id`), e as operações posteriores
// (cancelar, atualizar, baixar, PIX) autenticam na conta que a emitiu. O campo
// é obrigatório mesmo havendo uma única conta — a escolha é sempre explícita.
// ---------------------------------------------------------------------------

// O backend usa o STATUS para sinalizar duplicata: 201 = criada; 200 = já
// existia uma equivalente (a página abre o modal de confirmação). Preservar
// essa distinção é essencial — por isso `isDuplicate` sobe junto com o dado.
function readChargeResponse(response) {
   return {
      isDuplicate: response?.status === 200,
      charge: response?.data?.data?.charge ?? null,
      group: response?.data?.data?.group ?? null,
      message: response?.data?.message ?? null,
   };
}

// Boleto avulso. `payload` já vem com os campos do domínio (snake_case, igual
// ao contrato) — a página monta e converte os numéricos.
export async function createCharge(payload) {
   const response = await axios.post('/billing', payload);
   return readChargeResponse(response);
}

// Parcelamento: mesma semântica de duplicata, mas o sucesso devolve o GRUPO.
export async function createInstallment(payload) {
   const response = await axios.post('/billing/installment', payload);
   return readChargeResponse(response);
}

// ---------------------------------------------------------------------------
// LISTAGEM / EXPORT (handoff 0082)
//
// `bank_account_id` filtra por uma ou várias contas. O contrato canônico é CSV
// ("1,2,3") e vale para os três endpoints — por isso a serialização mora aqui,
// e as telas trabalham com array.
//
// Semântica garantida pelo backend: seleção vazia = SEM filtro por conta (a
// listagem volta ao normal, inclusive cobranças com bank_account_id NULL); id
// inexistente é ignorado; valor inválido é descartado sem virar 500.
// ---------------------------------------------------------------------------

function toBankAccountCsv(ids) {
   if (!Array.isArray(ids) || !ids.length) return undefined;
   return ids.join(',');
}

// Monta o corpo comum de search/export a partir do estado de filtro da tela
// (chaves já em snake_case = nomes do backend).
function buildSearchBody(filters = {}) {
   const body = {};
   if (filters.q) body.q = filters.q;
   if (filters.nfe_number) body.nfe_number = filters.nfe_number;
   if (filters.status) body.status = filters.status;
   if (filters.collection_status) {
      body.collection_status = filters.collection_status;
   }
   if (filters.origin_id) body.origin_id = parseInt(filters.origin_id, 10);
   if (filters.due_date_from) body.due_date_from = filters.due_date_from;
   if (filters.due_date_to) body.due_date_to = filters.due_date_to;

   // Ranges de recebimento e emissão (handoff 0106). "Emissão" filtra
   // asaas_created_at (a emissão real na ASAAS — created_at não é oferecido).
   // ⚠️ Os 6 params de data (inclui due_date_*) validam formato YYYY-MM-DD no
   // backend: malformado = 422 JSend com `field` por param (a tela acende
   // inline via fieldErrors). "" = ausência de filtro — daí o só-não-vazio.
   if (filters.payment_date_from) {
      body.payment_date_from = filters.payment_date_from;
   }
   if (filters.payment_date_to) body.payment_date_to = filters.payment_date_to;
   if (filters.asaas_created_from) {
      body.asaas_created_from = filters.asaas_created_from;
   }
   if (filters.asaas_created_to) {
      body.asaas_created_to = filters.asaas_created_to;
   }

   const bankAccountCsv = toBankAccountCsv(filters.bank_account_ids);
   if (bankAccountCsv) body.bank_account_id = bankAccountCsv;

   return body;
}

export async function searchCharges(filters, sort, page, perPage) {
   const body = {
      ...buildSearchBody(filters),
      sort_by: sort?.key ?? 'asaas_created_at',
      sort_direction: sort?.direction ?? 'desc',
      page,
      limit: perPage,
   };

   const response = await axios.post('/billing/search', body);
   const envelope = response?.data ?? {};
   const meta = envelope.meta ?? {};

   return {
      charges: envelope.data?.charges ?? [],
      total: meta.total ?? 0,
      totalPages: meta.total_pages ?? 1,
      // Soma respeita o mesmo `where` da listagem — o total exibido bate com as
      // linhas na tela, inclusive com filtro de conta.
      totalAmount: meta.total_amount ?? 0,
   };
}

// Mesmo conjunto de filtros da busca — se divergir, a planilha sai com outro
// recorte do que o operador está vendo (foi o que acontecia com `nfe_number`,
// ausente aqui até 2026-08-06).
export async function exportCharges(filters) {
   const response = await axios.post('/billing/export', buildSearchBody(filters), {
      responseType: 'blob',
   });
   return response.data;
}

// ---------------------------------------------------------------------------
// CATÁLOGO — ORIGENS (departamento gerador)
//
// 21 origens semeadas. Alimenta o select de emissão, o filtro da listagem e os
// chips do dashboard de vendas a prazo (handoff 0084 §3.2).
//
// ⚠️ Não há valor de `origin_id` que signifique "sem origem": a carga histórica
// (~71% do valor) tem origem NULL e o backend não expõe filtro para ela. Por
// isso o dashboard NÃO oferece chip "Sem classificação" no filtro — seria
// filtro fantasma (navegacao.md §1.0.3). O "Sem classificação" que aparece na
// dobra de breakdown é outra coisa: vem pronto no payload, como linha própria.
//
// As páginas BillingCharges/BillingCharge ainda chamam este endpoint inline com
// axios; migram para cá quando forem tocadas (FC1 A.10).
// ---------------------------------------------------------------------------

export async function listBillingOrigins() {
   const response = await axios.get('/billing/origins');
   return response?.data?.data?.origins ?? [];
}

// ---------------------------------------------------------------------------
// DETALHE + TIMELINE — migradas ao serem tocadas pelo handoff 0105 (campos de
// recebimento: amount_received / surcharge_amount / net_amount_received;
// FC1 A.10).
// ---------------------------------------------------------------------------

// Detalhe do título. Erros NÃO são tratados aqui de propósito: o caller lê
// err.response.status (404 → redirect para a listagem).
export async function getCharge(chargeId) {
   const response = await axios.get(`/billing/${chargeId}`);
   return response?.data?.data?.charge ?? null;
}

// Feed heterogêneo (update × change); os `change` já vêm com field_label
// pronto do backend — a tela renderiza sem mapear campo.
export async function getChargeTimeline(chargeId) {
   const response = await axios.get(`/billing/${chargeId}/timeline`);
   return response?.data?.data?.feed ?? [];
}

// ---------------------------------------------------------------------------
// CANCELAMENTO — `cancel_reason` obrigatório (mín. 8 caracteres após trim).
//
// ⚠️ DELETE com corpo no axios vai em `{ data: ... }`, não como 2º argumento
// (isso é `config`, e o payload seria descartado silenciosamente → 422). É a
// razão de as duas chamadas viverem aqui: o detalhe se esquece no 3º call site.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// BAIXA MANUAL + PIX — migradas ao serem tocadas pela Régua de Cobrança
// (handoff 0090; FC1 A.10). Desde o 0105 a página BillingCharge também baixa
// por aqui.
// ---------------------------------------------------------------------------

// Registra recebimento em dinheiro na ASAAS (receiveInCash). O STATUS do
// título/caso NÃO muda na resposta — só quando o webhook de pagamento chegar.
// Quem chama nunca deve pintar como pago no sucesso desta função. Desde o
// 0105 a resposta traz o charge com os campos de recebimento (amount_received
// etc.) — dado informativo, não sinal de "pago".
export async function settleCharge(chargeId, paymentDate) {
   const response = await axios.post(`/billing/${chargeId}/settle`, {
      payment_date: paymentDate,
   });
   return {
      message: response?.data?.message ?? null,
      charge: response?.data?.data?.charge ?? null,
   };
}

// PIX copia-e-cola do boleto. Chamar SÓ no gesto do usuário (nunca no mount):
// cada chamada consulta a ASAAS, que tem rate limit (handoff 0010).
export async function getChargePix(chargeId) {
   const response = await axios.get(`/billing/${chargeId}/pix`);
   const data = response?.data?.data ?? {};
   return {
      pixCopyPaste: data.pix_copy_paste ?? null,
      pixQrCodeUrl: data.pix_qr_code_url ?? null,
   };
}

export async function cancelCharge(chargeId, cancelReason) {
   const response = await axios.delete(`/billing/${chargeId}`, {
      data: { cancel_reason: String(cancelReason ?? '').trim() },
   });
   return { message: response?.data?.message ?? null };
}

// Lote: o MESMO motivo é propagado a todas as parcelas canceladas — é uma
// decisão única do operador, não uma por parcela.
//
// A resposta traz o resumo parcial em `data.message` como OBJETO
// ({ cancelled, errors }) — shape peculiar do endpoint, preservado aqui para
// não quebrar as duas telas que já leem assim.
export async function cancelChargeGroup(groupId, cancelReason) {
   const response = await axios.delete(`/billing/group/${groupId}`, {
      data: { cancel_reason: String(cancelReason ?? '').trim() },
   });
   const summary = response?.data?.message ?? {};
   return {
      cancelled: summary.cancelled ?? [],
      errors: summary.errors ?? [],
   };
}
